/**
 * AI Settings
 *
 * Replaces the messages area when open (no overlay clipping issues). Trace AI
 * holds the keys and picks the models server-side, so there is little here to
 * configure: who the user is signed in as, the workspace billed, what it costs,
 * the kits a session offers, and the MCP bridge.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { agentService } from './AgentService';
import { connectMCPBridge, disconnectMCPBridge, getMCPBridgeState, onMCPBridgeStateChange } from './mcp-bridge';
import type { MCPBridgeState } from './mcp-bridge';
import { isGatewayConfigured } from './gateway';
import { CreditsSection } from './CreditsDisplay';
import { KitSettings } from './KitSettings';
import { getCatalogEmail, getCatalogToken, handleLogout, onCatalogTokenChange } from '../catalog-auth';
import { useSharedTeam, useTeams } from '../catalog-tabs/state';
import { getShellService } from '../inspector-extension';
import * as styles from './chat-styles';

/**
 * Bring the catalog panel forward — it hosts the login form. The AI panel
 * doesn't own a second sign-in surface; there is one Trace account and one
 * place to enter it.
 */
export function openCatalogPanel(): void {
    getShellService()?.sidePanes.find(p => p.key === 'catalog-panel')?.select();
}

interface ApiKeySettingsProps {
    onClose: () => void;
}

/** Shared width for all label columns so they align across sections */
const LABEL_WIDTH = 80;

export const ApiKeySettings: React.FC<ApiKeySettingsProps> = ({ onClose }) => {
    const [mcpState, setMcpState] = useState<MCPBridgeState>(getMCPBridgeState);
    const teams = useTeams();
    const [selectedTeamId, setSelectedTeamId] = useSharedTeam();

    useEffect(() => onMCPBridgeStateChange(() => setMcpState(getMCPBridgeState())), []);
    // Sign-in/out changes what the panel shows.
    const [, setAuthTick] = useState(0);
    useEffect(() => onCatalogTokenChange(() => setAuthTick(n => n + 1)), []);

    const handleMcpToggle = useCallback(() => {
        if (mcpState !== 'off') {
            disconnectMCPBridge();
        } else {
            connectMCPBridge(
                () => agentService.getScene(),
                () => agentService.getScene()?.getSelectedObject() ?? null,
                () => agentService.getViewer(),
            );
        }
    }, [mcpState]);

    const sectionLabel: React.CSSProperties = {
        ...styles.settingsLabel,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: styles.colors.textMuted,
        marginTop: 4,
    };

    const rowLabel: React.CSSProperties = {
        fontSize: 12,
        color: styles.colors.textMuted,
        width: LABEL_WIDTH,
        flexShrink: 0,
    };

    const selectStyle: React.CSSProperties = {
        ...styles.settingsInput,
        padding: '4px 6px',
        cursor: 'pointer',
        appearance: 'auto',
        fontSize: 12,
    };

    const traceAI = isGatewayConfigured();
    const signedIn = getCatalogToken() !== null;
    const email = getCatalogEmail();

    return (
        <div style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '12px 10px',
            background: styles.colors.bg,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
        }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: styles.colors.textBright }}>Settings</span>
                <button
                    onClick={onClose}
                    style={{ ...styles.iconButton, fontSize: 16 }}
                    title="Close settings"
                >
                    {'\u2715'}
                </button>
            </div>

            {/* No gateway in this build. VITE_AI_GATEWAY_URL is inlined at build
                time, so naming it is the only way a dev running a local build
                can tell this apart from being signed out. */}
            {!isGatewayConfigured() && (
                <div style={{ fontSize: 11, color: styles.colors.textMuted, lineHeight: 1.5 }}>
                    Trace AI is unavailable in this build — no gateway was configured
                    at build time (<code>VITE_AI_GATEWAY_URL</code>), so the assistant
                    cannot run here.
                </div>
            )}

            {/* Account: Trace AI runs on the Trace sign-in. */}
            {traceAI && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={sectionLabel}>Account</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={rowLabel}>Trace</span>
                        {signedIn ? (
                            <>
                                <span style={{ fontSize: 12, color: styles.colors.textBright, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {email ?? 'Signed in'}
                                </span>
                                <button
                                    onClick={() => { handleLogout(); setAuthTick(n => n + 1); }}
                                    style={{ ...styles.smallButton, padding: '3px 8px', fontSize: 11 }}
                                >
                                    Sign out
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={openCatalogPanel}
                                    style={{
                                        ...styles.smallButton,
                                        padding: '4px 12px',
                                        fontSize: 11,
                                        background: styles.colors.accent,
                                        color: '#fff',
                                        borderRadius: 4,
                                    }}
                                >
                                    Sign in
                                </button>
                                <span style={{ fontSize: 11, color: styles.colors.textMuted, flex: 1 }}>
                                    Trace AI needs a Trace account — opens the catalog panel
                                </span>
                            </>
                        )}
                    </div>
                    {/* The workspace AI usage is billed to. Shares the catalog's
                        selection, so switching here re-scopes the whole editor —
                        which is the honest behaviour: there is one active
                        workspace, not a separate one for the assistant. */}
                    {signedIn && teams.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={rowLabel}>Workspace</span>
                            <select
                                value={selectedTeamId ?? ''}
                                onChange={(e) => setSelectedTeamId(e.target.value || null)}
                                disabled={teams.length === 1}
                                style={{
                                    ...selectStyle,
                                    flex: 1,
                                    cursor: teams.length === 1 ? 'default' : 'pointer',
                                    opacity: teams.length === 1 ? 0.7 : 1,
                                }}
                                title={teams.length === 1 ? 'Active workspace' : 'Switch workspace'}
                            >
                                {teams.map(t => (
                                    <option key={t.teamId} value={t.teamId ?? ''}>{t.name ?? 'Unnamed workspace'}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            )}

            {/* Credits — spent server-side. Renders nothing until the ledger answers. */}
            {traceAI && (
                <CreditsSection
                    sectionLabelStyle={sectionLabel}
                    rowLabelStyle={rowLabel}
                />
            )}

            <KitSettings labelStyle={sectionLabel} />

            {/* MCP Bridge section */}
            <div style={{
                borderTop: `1px solid ${styles.colors.border}`,
                paddingTop: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
            }}>
                <label style={sectionLabel}>MCP Bridge</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={rowLabel}>Live</span>
                    <button
                        onClick={handleMcpToggle}
                        style={{
                            ...styles.smallButton,
                            padding: '4px 12px',
                            fontSize: 11,
                            background: mcpState === 'connected' ? styles.colors.success
                                : mcpState === 'connecting' ? '#c90'
                                : styles.colors.bgInput,
                            color: mcpState !== 'off' ? '#fff' : styles.colors.text,
                            borderRadius: 4,
                            transition: 'background 0.15s',
                        }}
                    >
                        {mcpState === 'connected' ? 'Connected' : mcpState === 'connecting' ? 'Connecting…' : 'Start'}
                    </button>
                    <span style={{ fontSize: 11, color: styles.colors.textMuted }}>
                        {mcpState === 'connected'
                            ? 'Bridge active on ws://127.0.0.1:4097'
                            : mcpState === 'connecting'
                            ? 'Waiting for MCP server…'
                            : 'Connect to external MCP clients'}
                    </span>
                </div>
            </div>

        </div>
    );
};
